// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { generateLinksFrom } from "@insite/content-library/Components/PageBreadcrumbs";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useContentSearchDataView } from "@store/Framework/Store/Data/ContentSearch/ContentSearchHooks";
import { useContentSearchParameter } from "@store/Framework/Store/Starter/Pages/ProductList/ProductListHooks";
import ContentSearchResultsListPagination, {
    ContentSearchResultsListPaginationPresentationProps,
} from "@store/Library/Components/Toolkit/ContentSearch/ContentSearchResultsListPagination";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useHomePageUrl } from "@toolkit/Framework/Store/Links/LinksHooks";
import { useSearchQuery } from "@toolkit/Framework/Store/Toolkit/Pages/ProductList/ProductListHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import parse from "html-react-parser";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

interface ContentSearchResultsListProps extends WidgetProps {}

export interface ContentSearchResultsListStyles {
    loadingOverlay?: LoadingOverlayProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    noQueryText?: TypographyProps;
    countWrapper?: InjectableCss;
    countText?: TypographyProps;
    resultsList?: InjectableCss;
    resultsListItem?: InjectableCss;
    breadcrumbs?: BreadcrumbsPresentationProps;
    itemLink?: LinkPresentationProps;
    itemTitle?: TypographyPresentationProps;
    itemSummary?: TypographyPresentationProps;
    contentSearchResultsListPagination?: ContentSearchResultsListPaginationPresentationProps;
}

export const contentSearchResultsListStyles: ContentSearchResultsListStyles = {
    noResultsText: typographyGetNoResultsAltProps({ separated: true }),
    noQueryText: typographyGetNoResultsAltProps(),
    countWrapper: resultCountProps.wrapper,
    countText: resultCountProps.countText,
    resultsList: {
        css: css`
            display: flex;
            flex-direction: column;
            border-top: ${pxToRem(1)} solid ${getColor("common.border")};
        `,
    },
    resultsListItem: {
        css: css`
            padding: ${pxToRem(24, 0)};
            border-bottom: 1px solid ${getColor("common.border")};

            em {
                font-style: normal;
                background-color: rgba(255, 255, 0, 0.5);
            }
        `,
    },
    breadcrumbs: {
        mergeCss: true,
        css: css`
            & {
                padding: 0;
            }
        `,
    },
    itemTitle: {
        forwardAs: "h2",
        variant: "h4",
        css: css`
            ${spacingStyles(pxToRem(8), pxToRem(8))}
        `,
    },
};

const styles = contentSearchResultsListStyles;

const ContentSearchResultsList: FC<ContentSearchResultsListProps> = () => {
    const baseContentSearchDataView = useContentSearchDataView();
    const contentSearchDataView = usePersistentDataView(baseContentSearchDataView);
    const { value: results } = contentSearchDataView;
    const resultsCount = (contentSearchDataView as any)?.pagination?.totalItemCount ?? 0;
    const resultsCountLabel = resultsCount === 1 ? translate("Result") : translate("Results");
    const contentSearchParams = useContentSearchParameter();
    const homePageUrl = useHomePageUrl();
    const searchQuery = useSearchQuery();
    const emphasisRegex = new RegExp(`(${contentSearchParams.query})`, "gi");
    const links = useAppSelector(state => state.links);
    const breadcrumbsLinks = results?.map(x => generateLinksFrom(links, x.nodeId, homePageUrl)) || [];
    const pagesLink =
        useAppSelector(state =>
            results?.map(x => ({
                href: getPageLinkByNodeId(state, x.nodeId)?.url,
            })),
        ) || [];
    const noResultsText = translate("There were no results found.");

    if (!searchQuery) {
        return <Typography {...styles.noQueryText}>{noResultsText}</Typography>;
    }

    return (
        <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={contentSearchDataView}>
            <StyledWrapper {...styles.countWrapper}>
                <Typography {...styles.countText}>
                    {resultsCount} {resultsCountLabel}
                </Typography>
            </StyledWrapper>
            {!results?.length ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText}>{noResultsText}</Typography>
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...styles.resultsList} as="ul">
                        {results.map(({ fields: { metaDescription, title }, id }, index) => {
                            if (!pagesLink[index].href) {
                                return;
                            }
                            const titleWithEmphasis = title.replace(emphasisRegex, "<em>$1</em>");
                            const descriptionWithEmphasis = metaDescription
                                ? metaDescription.replace(emphasisRegex, "<em>$1</em>")
                                : "";
                            return (
                                <StyledWrapper {...styles.resultsListItem} as="li" key={id}>
                                    <Breadcrumbs {...styles.breadcrumbs} links={breadcrumbsLinks[index]} />
                                    <Typography {...styles.itemTitle}>
                                        <Link {...styles.itemLink} href={pagesLink[index].href}>
                                            {parse(titleWithEmphasis, parserOptions)}
                                        </Link>
                                    </Typography>
                                    {metaDescription ? (
                                        <Typography {...styles.itemSummary}>
                                            {parse(descriptionWithEmphasis, parserOptions)}
                                        </Typography>
                                    ) : null}
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                    <ContentSearchResultsListPagination {...styles.contentSearchResultsListPagination} />
                </>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: ContentSearchResultsList,
    definition: {
        displayName: "Content Search Results",
        group: "Product List",
        icon: "list-ul",
        fieldDefinitions: [],
    },
};

export default widgetModule;
